import React from 'react';
import { Product, ProductGroup, WithContext } from 'schema-dts';
import { ReviewRatingStatistics } from 'shared/types/product/Product';
import { ExtendedVariant } from 'shared/types/product/Variant';
import { transformCloudinaryUrl } from 'shared/types/utils/cloudinary';
import { UIProduct } from 'components/commercetools-ui/organisms/product/product-details/types';
import { mapReturnPolicy } from './returnPolicy';
import { mapProductImages, mapProductOffer, mapProductRatings, mapProductReview } from './utils';
import { ReviewListProps } from '../../../components/commercetools-ui/organisms/review-list';
import { getProductDescription, getProductDisplayName } from '../../metadata/productDescription';
import { productNoun, variantDisplayNames, variantPath, variantPropertyValues, variesByFor } from '../../seo/variant-seo';
import { textToColor } from '../../textToColor/textToColor';

export interface ProductJSONLDProps {
  variant: ExtendedVariant;
  product: UIProduct;
  description?: string
  ratingData?: ReviewRatingStatistics;
  finalBasePrice: number;
  reviews?: ReviewListProps;
  origin?: string;
  locale: string;
}

// Google wants one image per variant; the page-level Product already carries the full set, so the
// group keeps each sibling to a few rather than repeating every shot for every sibling.
const MAX_VARIANT_IMAGES = 3;

const resizedImages = (variant: ExtendedVariant) =>
  (variant.images ?? [])
    .map((url) => transformCloudinaryUrl(url, { width: 500 }) ?? url)
    .filter(Boolean)
    .slice(0, MAX_VARIANT_IMAGES);

// Google rejects a variant Product without an image (critical in the Rich Results Test). A sibling
// with no shots of its own borrows a same-colour sibling's, then the first sibling that has any. It
// never borrows from the page's own variant (`product.images`), or the group would publish a
// different, wrong-colour image for the same SKU depending on which page it is on.
// Donors are judged by their usable (resized, non-empty) images, so a sibling whose only entries are
// blank never wins over one with a real shot.
const variantImagesBySku = (variants: ExtendedVariant[]) => {
  const own = variants.map((v) => ({ variant: v, images: resizedImages(v) }));
  const firstWithImages = own.find((o) => o.images.length)?.images ?? [];
  const byColor = new Map<string, string[]>();
  for (const { variant, images } of own) {
    const color = variant.attributes?.color;
    if (color && images.length && !byColor.has(color)) byColor.set(color, images);
  }
  return new Map(
    own.map(({ variant, images }) => {
      const color = variant.attributes?.color;
      const borrowed = (color && byColor.get(color)) || firstWithImages;
      return [variant.sku, images.length ? images : borrowed];
    }),
  );
};

/**
 * SEO Data will be generated for the master variant, which is the first
 * @param product
 * @param reviews
 * @param ratingData
 * @param locale
 * @constructor
 */
export const ProductJSONLD = ({
  product,
  reviews,
  description,
  ratingData,
  origin,
  variant,
  finalBasePrice,
}: ProductJSONLDProps) => {
  const productImages = mapProductImages(product);
  // The assigned lifestyle photo (`feed_lifestyle:<slug>`) goes after the product's own shots, so the
  // product itself stays the lead image Google picks.
  const lifestyleImage = product.feedLifestyleImage
    ? transformCloudinaryUrl(product.feedLifestyleImage, { width: 500 })
    : undefined;
  const image: Product['image'] =
    lifestyleImage && !productImages.includes(lifestyleImage) ? [...productImages, lifestyleImage] : productImages;
  const offers: Product['offers'] = mapProductOffer(product, variant, finalBasePrice, origin);
  const color: Product['color'] = textToColor(variant.attributes?.color).label;
  const displayName = getProductDisplayName(product);
  // product.slug, like the sitemap and ProductRouter: it is what the `/slug/p/<sku>/` URL is built from.
  const slug = product.slug;
  const variants = product.variants ?? [];
  // The one name each variant is linked and titled by everywhere (page title, option links, "More …").
  const names = variantDisplayNames(product);
  const isGroup = variants.length > 1 && !!slug;
  const groupId = slug ?? '';
  const imagesBySku = isGroup ? variantImagesBySku(variants) : null;

  const productJsonLd: WithContext<Product> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    // The page is for THIS variant, so the Product names it (the first variant's name used to be
    // published on every sibling's page). The parent groups the siblings below.
    name: names.get(variant.sku) ?? variant.name ?? displayName,
    sku: variant.sku,
    ...(isGroup && { inProductGroupWithID: groupId }),
    ...(origin && slug && { url: `${origin}${variantPath(slug, variant.sku)}` }),
    offers,
    image,
    ...(color && { color }),
    brand: {
      '@type': 'Brand',
      name: 'Viewrail',
    },
    itemCondition: 'https://schema.org/NewCondition',
    description: getProductDescription(description, displayName),
    ...(ratingData && { aggregateRating: mapProductRatings(ratingData) }),
    ...(reviews && { review: mapProductReview(reviews) }),
  };

  // One ProductGroup per parent, on every variant page, listing every sibling with its own URL and
  // offer: this is how search engines learn that six `/p/<sku>/` pages are one product with colour
  // and size options (schema.org/ProductGroup, Google's "product variants" structured data).
  const groupJsonLd: WithContext<ProductGroup> | null = isGroup
    ? {
        '@context': 'https://schema.org',
        '@type': 'ProductGroup',
        productGroupID: groupId,
        name: productNoun(product),
        ...(origin && { url: `${origin}${variantPath(groupId, variants[0].sku)}` }),
        brand: { '@type': 'Brand', name: 'Viewrail' },
        description: getProductDescription(description, product.name ?? displayName),
        variesBy: variesByFor(product),
        hasVariant: variants.map((sibling) => {
          const siblingUrl = origin ? `${origin}${variantPath(groupId, sibling.sku)}` : undefined;
          const siblingImages = imagesBySku?.get(sibling.sku) ?? [];
          const siblingReturnPolicy = mapReturnPolicy(product, sibling);
          return {
            '@type': 'Product',
            sku: sibling.sku,
            name: names.get(sibling.sku) ?? sibling.name ?? displayName,
            ...(siblingImages.length && { image: siblingImages }),
            brand: { '@type': 'Brand', name: 'Viewrail' },
            ...(siblingUrl && { url: siblingUrl }),
            ...variantPropertyValues(product, sibling),
            // A sibling without a price (quote-only) publishes no offer rather than a $0 one.
            // Siblings carry no shippingDetails on purpose: each variant's own page publishes them, and
            // repeating ~0.5 KB per sibling would add >100 KB to the 252-variant post pages.
            ...(sibling.price?.centAmount && {
              offers: {
                '@type': 'Offer',
                priceCurrency: product?.price?.currencyCode ?? sibling.price.currencyCode ?? 'USD',
                price: sibling.price.centAmount / 100,
                availability: sibling.isAvailable === false ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock',
                ...(siblingUrl && { url: siblingUrl }),
                ...(siblingReturnPolicy && { hasMerchantReturnPolicy: siblingReturnPolicy }),
              },
            }),
          };
        }),
      }
    : null;

  {
    /* script with dangerously set inner html as based on the NextJS site:
          https://nextjs.org/docs/app/building-your-application/optimizing/metadata#json-ld
          */
  }
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(productJsonLd) }} />
      {groupJsonLd && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(groupJsonLd) }} />}
    </>
  );
};
